// MACHINERY V2 (Stroje) — the 9 canonical card-state contract (M2.1).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §4.3 (LOCKED) + §3.
// Reference impl: design_handoff_machinery_v3/machinery-live.jsx (STATUS5 +
// StatusBadge / ConnectivityDot / AlarmStrip / ProgramLine) and
// machinery-states.jsx (STATE_SPECS / StateCard).
//
// THIS FILE IS THE SINGLE SOURCE OF STATUS TRUTH for v2. Fleet cards (M2.2),
// grouped rows (M2.3) and the profile hero ribbon (M2.4) render through
// deriveCardPresentation + these style builders — they never re-derive a state,
// colour, icon or label, and never re-test raw telemetry fields.
//
// Locked rules encoded here (§4.3):
//   - Every connected state carries BOTH a colour AND an icon (colour-blind
//     safety) — see STATUS_SPECS: no entry may omit its icon.
//   - The connectivity dot is a SEPARATE signal (ConnDot.tsx); an unconnected
//     machine (conn === null) renders NOTHING — no badge, no dot.
//   - Stale desaturates the badge AND ambers the conn dot TOGETHER: both
//     degrade from `conn` alone. The handoff model carried a redundant `stale`
//     boolean next to `conn`; it is deliberately NOT in our typed model.
//   - The alarm strip is a conditional row and NEVER animates (AlarmStrip.tsx).
//   - The program line renders only while running (deriveCardPresentation).
//   - Alarms beat the STATUS FIELD; connectivity verdicts beat alarms.
//
// Variants are plain lookup maps to inline style objects (§3: NOT cva).
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
// (Style builders live in cardStateStyles.ts — split out at M2.2, see that
// file's header. cardStates.ts owns the DERIVATION; nothing here paints.)

// ============================================================
// Closed enums
// ============================================================

/** The 5 telemetry statuses a connected machine can carry. `offline` is
 *  server-flipped after ~60 s of silence, not agent-reported. */
export type MachineryV2Status = "running" | "idle" | "stopped" | "alarm" | "offline";

/** Connectivity track — independent of the status above.
 *  awaiting  = connection row exists, agent has never sent data
 *  live      = heartbeat fresh
 *  stale     = heartbeat past the freshness window (last-known data)
 *  longstale = silent long enough that nothing on the card is trustworthy
 *  null      = NO MachineConnection row at all (99% of the fleet). */
export type MachineryV2Conn = "awaiting" | "live" | "stale" | "longstale";

/** The 9 canonical card states (§4.3) — the contract name-set.
 *  `fleetStatusFilters` in machineryV2UiSlice narrows to this at the call
 *  site (the slice stays string[] so it carries no dependency on M2.1). */
export type MachineryV2CardState =
  | "unconnected"
  | "awaiting"
  | "running"
  | "idle"
  | "stopped"
  | "alarm"
  | "stale"
  | "stale-alarm"
  | "offline";

/** Canonical order — the artboard rules grid and the M2.2 filter rail. */
export const CARD_STATE_ORDER: readonly MachineryV2CardState[] = [
  "unconnected", "awaiting", "running", "idle", "stopped",
  "alarm", "stale", "stale-alarm", "offline",
];

export type MachineryV2IconKind =
  | "play"
  | "pause"
  | "square"
  | "alert"
  | "cloudoff"
  | "hourglass";

/** Palette CSS vars a status may paint with. Closed on purpose. */
export type MachineryV2StatusToken =
  | "--green"
  | "--amber"
  | "--muted"
  | "--red"
  | "--faint";

// ============================================================
// STATUS_SPECS — one entry per status: colour + icon + i18n key
// ============================================================

export interface MachineryV2StatusSpec {
  token: MachineryV2StatusToken;
  icon: MachineryV2IconKind;
  /** i18next key (myMachinery namespace); rendered uppercase by CSS. */
  labelKey: string;
}

export const STATUS_SPECS: Record<MachineryV2Status, MachineryV2StatusSpec> = {
  running: { token: "--green", icon: "play", labelKey: "myMachinery:v2.states.running" },
  idle: { token: "--amber", icon: "pause", labelKey: "myMachinery:v2.states.idle" },
  stopped: { token: "--muted", icon: "square", labelKey: "myMachinery:v2.states.stopped" },
  alarm: { token: "--red", icon: "alert", labelKey: "myMachinery:v2.states.alarm" },
  offline: { token: "--faint", icon: "cloudoff", labelKey: "myMachinery:v2.states.offline" },
};

// ============================================================
// Typed model — M2.2 adapts the gateway payload onto this
// ============================================================

export interface MachineryV2Alarm {
  /** Control-system alarm code, e.g. "Alarm 700057". */
  code: string;
  message: string;
  /** Pre-formatted clock/relative string from the adapter, e.g. "23:18". */
  since: string | null;
}

export interface MachineryV2Telemetry {
  conn: MachineryV2Conn | null;
  status: MachineryV2Status | null;
  /** Clock time the current status started, e.g. "06:49". */
  since: string | null;
  /** Elapsed-in-status, e.g. "2h 14m". */
  elapsed: string | null;
  /** Human heartbeat age, e.g. "pred 4 s" — surfaced on the conn-dot title. */
  heartbeat: string | null;
  program: string | null;
  partCount: number | null;
  spindleRpm: number | null;
  feedPercent: number | null;
  alarms: MachineryV2Alarm[];
}

/** Identity + telemetry a status-contract card needs. M2.2's fleet card
 *  extends this with photo, capability badges (M2.9), kebab actions etc. */
export interface MachineryV2CardMachine {
  id: string;
  brand: string;
  model: string;
  typeLabel: string;
  serialNumber: string | null;
  controlSystem: string | null;
  locationLabel: string | null;
  subLocationLabel: string | null;
  telemetry: MachineryV2Telemetry;
}

/** The default M2.2 uses for every machine the gateway says nothing about.
 *  A factory, not a shared constant, so no caller can mutate its alarms. */
export const unconnectedTelemetry = (): MachineryV2Telemetry => ({
  conn: null, status: null, since: null, elapsed: null, heartbeat: null,
  program: null, partCount: null, spindleRpm: null, feedPercent: null, alarms: [],
});

// ============================================================
// Derivations — the ONLY place a card state is decided
// ============================================================

/** Staleness has ONE source: the connectivity track. Badge desaturation and
 *  the amber conn dot both degrade from this same input (§4.3), so they can
 *  never diverge. */
export const isStale = (conn: MachineryV2Conn | null): boolean =>
  conn === "stale" || conn === "longstale";

/** An alarm payload OUTRANKS THE STATUS FIELD — and only that; it does NOT
 *  outrank a connectivity verdict (step 3 below). A machine reporting
 *  "running" while it carries alarms IS in the alarm state, or a green RUNNING
 *  badge sits above a red alarm strip and the row escapes an alarm filter. */
const alarming = (t: MachineryV2Telemetry): boolean =>
  t.alarms.length > 0 || t.status === "alarm";

/** Resolve one of the 9 canonical states. CONNECTIVITY VERDICTS FIRST:
 *   1. no connection row      → unconnected (renders NOTHING at all)
 *   2. agent never reported   → awaiting
 *   3. server-flipped offline → offline. ABOVE the alarm branch ON PURPOSE
 *      (2026-08-22): a server verdict is never re-labelled. Alarms winning here
 *      filed a DEAD machine as "stale-alarm", invisible to the M2.2 offline
 *      filter. Not dropped — the strip shows, marked last-known.
 *   4. alarms present         → alarm / stale-alarm (beats the status field)
 *   5. stale connectivity     → stale. ABOVE the null-status branch ON PURPOSE
 *      (2026-08-22, ruling A): TelemetryStale adapts to conn "stale" with NO
 *      status (the summary endpoint carries no last-known one), so the old
 *      ordering fell through to `awaiting` — mislabelling a machine that HAD
 *      been reporting, AND filing it in the `awaiting` filter cell, which left
 *      the "stale data" counter permanently 00 filtering nothing. Accepted
 *      consequence: `stale` + null status has badgeStatus null, so the card
 *      shows an amber dot and no badge ("connected, data stale, status
 *      unknown"). Self-heals when the deferred backend (§6/§7) sends a status.
 *   6. connected but no readable status → awaiting. NOT "unconnected": the
 *      agent is reachable and heartbeating. Still reached by the adapter's
 *      UNREADABLE row (conn "live", status null) — only a STALE conn overtakes.
 *  Nothing outside this function decides a card state. */
export const deriveCardState = (t: MachineryV2Telemetry): MachineryV2CardState => {
  if (t.conn === null) return "unconnected";
  if (t.conn === "awaiting") return "awaiting";
  if (t.status === "offline") return "offline";
  const stale = isStale(t.conn);
  if (alarming(t)) return stale ? "stale-alarm" : "alarm";
  if (stale) return "stale";
  if (t.status === null) return "awaiting";
  // Unreachable with a null status: the branch above already returned.
  return t.status;
};

/** Everything a card paints, derived ONCE from the telemetry.
 *  Consumers (StateCard now; FleetCard / FleetRow / ProfileHero later) read
 *  THESE FLAGS and must not re-test raw telemetry fields. The split brain that
 *  discipline prevents is real: a component testing `alarms.length` while the
 *  state machine tested `status === "alarm"` produced a RUNNING badge above an
 *  alarm strip. */
export interface MachineryV2CardPresentation {
  state: MachineryV2CardState;
  /** Status the badge renders; null = no status badge at all. */
  badgeStatus: MachineryV2Status | null;
  /** Desaturate the badge + show the "?" marker. NEVER true for `offline`:
   *  the slate cloud-off badge already IS the staleness verdict, and
   *  desaturating it would strip the very colour+icon that identify it. */
  staleLook: boolean;
  showAwaitingBadge: boolean;
  showStatusRow: boolean;
  /** Also TRUE for `offline` + alarms: STATE filters, PRESENTATION shows. */
  showAlarmStrip: boolean;
  /** Last-known prefix — `stale-alarm` + `offline`, never a live alarm. Pass
   *  THIS to AlarmStrip, NOT `staleLook` (false for offline, by design). */
  alarmLastKnown: boolean;
  showProgramLine: boolean;
  showNoTelemetry: boolean;
  /**
   * The connectivity dot's state — DERIVED here, never the raw `t.conn`.
   *
   * M2.2 defect fix. The dot used to be the ONE thing a card read straight off
   * telemetry (`<ConnDot state={t.conn} />`), which put it outside the single
   * derivation and let it contradict the badge it sits next to. With M2.2's
   * gateway adapter that stopped being theoretical: the wire's `Offline`
   * verdict arrives as `status:"offline"` on a machine that IS still
   * heartbeating (`conn:"live"`), so the card painted a slate cloud-off
   * OFFLINE badge beside a green, PULSING "live" dot.
   *
   * `conn:"live"` is the truthful INPUT — an offline-flagged machine really is
   * reachable — so the fix belongs here, in presentation, not in the adapter's
   * mapping table. For `offline` the dot degrades to `longstale` (faint grey +
   * X knockout): badge and dot then both read "do not trust this machine", and
   * the green/pulse path is unreachable for that state by construction.
   * Every other state keeps today's behaviour exactly.
   */
  dotConn: MachineryV2Conn | null;
}

/** `dotConn`, in one place. unconnected/awaiting show no dot at all (§4.3 —
 *  awaiting has its own badge and no heartbeat to grade yet); offline is
 *  forced to the grey+X treatment; everything else passes the real
 *  connectivity track through unchanged. */
const dotConnFor = (
  state: MachineryV2CardState,
  conn: MachineryV2Conn | null
): MachineryV2Conn | null => {
  if (state === "unconnected" || state === "awaiting") return null;
  if (state === "offline") return "longstale";
  return conn;
};

export const deriveCardPresentation = (
  t: MachineryV2Telemetry
): MachineryV2CardPresentation => {
  const state = deriveCardState(t);
  const isAlarm = state === "alarm" || state === "stale-alarm";
  const staleLook = state === "stale" || state === "stale-alarm";
  const badgeStatus: MachineryV2Status | null =
    state === "unconnected" || state === "awaiting"
      ? null
      : isAlarm
      ? "alarm"
      : state === "stale"
      ? t.status
      : state;
  return {
    state,
    badgeStatus,
    staleLook,
    showAwaitingBadge: state === "awaiting",
    showStatusRow: state !== "unconnected",
    showAlarmStrip: (isAlarm || state === "offline") && t.alarms.length > 0,
    alarmLastKnown: state === "stale-alarm" || state === "offline",
    // Reads the underlying status, not the state name: a STALE machine that is
    // still running keeps its (last-known) program line, exactly as artboard
    // state 07 does. An alarm always suppresses it.
    showProgramLine: !isAlarm && t.status === "running" && !!t.program,
    showNoTelemetry: state === "unconnected",
    dotConn: dotConnFor(state, t.conn),
  };
};
